import { useState } from 'react'
import { classNames, getDisplayName, getAvatarInitial } from '@/lib/utils'

interface Props {
  profile?: {
    firstName?: string | null
    lastName?: string | null
    photo?: string | null
    emailAddresses?: Array<{ emailAddress: string; primary?: boolean }> | null
  } | null
  size?: 'xs' | 'sm' | 'md' | 'lg'
  className?: string
}

const sizeClasses = {
  xs: 'h-6 w-6 text-[10px]',
  sm: 'h-8 w-8 text-xs',
  md: 'h-10 w-10 text-sm',
  lg: 'h-12 w-12 text-lg',
}

export function UserAvatar({ profile, size = 'sm', className }: Props) {
  // Tracks the specific URL that failed, not just a boolean — if `profile.photo` later changes
  // to a different URL (e.g. this instance gets reused for another profile), that's a fresh
  // attempt rather than staying stuck on the old failure.
  const [failedSrc, setFailedSrc] = useState<string | null>(null)
  const displayName = getDisplayName(profile)
  const initial = getAvatarInitial(displayName)

  if (profile?.photo && profile.photo !== failedSrc) {
    return (
      <img
        src={profile.photo}
        alt={displayName}
        onError={() => setFailedSrc(profile.photo ?? null)}
        className={classNames(sizeClasses[size], 'rounded-full object-cover', className)}
      />
    )
  }

  return (
    <div
      className={classNames(
        sizeClasses[size],
        'flex items-center justify-center rounded-full bg-primary-100 font-medium text-primary-600',
        className,
      )}
    >
      {initial}
    </div>
  )
}
